import PropTypes from 'prop-types';
import { Icon, Tooltip2, ButtonTooltip } from '@evertrue/et-components';

/**
 * Maps DS ratings to their descriptive subtext.
 */
const DS_RATING_SUBTEXT = {
  '1-1': 'Based on a $5K+ gift',
  '1-2': 'Based on major wealth indicators',
  '1-3': 'Based on moderate wealth markers',
  '1-4': 'Based on emerging wealth markers',
  '1-5': 'Based on executive leadership',
  3: 'No identified wealth/giving indicators',
};

const DS_RATING_TOOLTIP_INTRO =
  "The DS Rating is DonorSearch's measure of a constituent's philanthropic activity and wealth indicators, based on publicly available data.";

/**
 * Maps DS ratings to their full tooltip content.
 */
const DS_RATING_TOOLTIPS = {
  '1-1': `${DS_RATING_TOOLTIP_INTRO}\n\nDS 1-1 is the highest rating and indicates documented philanthropic giving. This includes gifts of $5,000 or more to a nonprofit or political organization and may also include additional wealth indicators.`,
  '1-2': `${DS_RATING_TOOLTIP_INTRO}\n\nDS 1-2 is the second-highest rating and indicates strong wealth indicators. Examples include $2M+ in real estate holdings, executive leadership at a $5M+ organization, foundation trustee roles, SEC insider status, or $10K+ in political contributions.`,
  '1-3': `${DS_RATING_TOOLTIP_INTRO}\n\nDS 1-3 is the third-highest rating and indicates moderate wealth indicators. Examples include $1–2M in real estate, executive leadership at a $1–5M organization, or $5K–$10K in political contributions.`,
  '1-4': `${DS_RATING_TOOLTIP_INTRO}\n\nDS 1-4 is the fourth-highest rating and indicates emerging wealth or early giving signals. Examples include $500K–$1M in real estate, ownership of three or more properties, or a single gift of $500 or more.`,
  '1-5': `${DS_RATING_TOOLTIP_INTRO}\n\nDS 1-5 is the fifth-highest rating and indicates limited wealth indicators. Examples include executive leadership at a $1M+ organization, SEC insider status, or service as a board member.`,
  3: `${DS_RATING_TOOLTIP_INTRO}\n\nDS 3 indicates that DonorSearch has not identified significant wealth or philanthropic giving indicators for this constituent.`,
};

/**
 * Renders tooltip content with proper line breaks for multi-paragraph text.
 */
const renderTooltipContent = (text) => {
  if (!text) return null;
  const paragraphs = text.split('\n\n');
  return paragraphs.map((paragraph, index) => (
    <p key={index} style={{ margin: index === 0 ? 0 : '8px 0 0 0' }}>
      {paragraph}
    </p>
  ));
};

const CapacityRatingsCard = ({ dsRating, capacityWealth, capacityGiving, capacityLoading = false }) => {
  // Normalize rating for lookups (e.g., "DS1-4" → "1-4")
  const ratingKey = dsRating ? dsRating.replace('DS', '') : null;
  const dsRatingSubtext = ratingKey ? DS_RATING_SUBTEXT[ratingKey] : null;
  const dsRatingTooltip = ratingKey ? DS_RATING_TOOLTIPS[ratingKey] : DS_RATING_TOOLTIP_INTRO;

  // Format display label (e.g., "DS1-4" or "1-4" → "DS 1-4")
  const dsRatingDisplay = ratingKey ? `DS ${ratingKey}` : 'DS N/A';

  return (
    <div className="capacity-ratings-card">
      <div className="capacity-ratings-card--header">
        <div className="capacity-ratings-card--header-left">
          <Icon icon="star" />
          <span className="capacity-ratings-card--title">Capacity Ratings</span>
        </div>
        <div className="capacity-ratings-card--header-right">
          <div className="capacity-ratings-card--ds-rating">
            <span className="capacity-ratings-card--ds-label">{dsRatingDisplay}</span>
            <Tooltip2
              trigger={
                <span className="capacity-ratings-card--ds-info-icon">
                  <Icon icon="info-circle" iconKit="gt2" />
                </span>
              }
              contentWidth={320}
            >
              {renderTooltipContent(dsRatingTooltip)}
            </Tooltip2>
          </div>
          {dsRatingSubtext && <span className="capacity-ratings-card--ds-subtext">{dsRatingSubtext}</span>}
        </div>
      </div>

      <div className="capacity-ratings-card--content">
        <div className="capacity-ratings-card--metrics">
          <div className="capacity-ratings-card--metric">
            <div className="capacity-ratings-card--metric-label-wrapper">
              <span className="capacity-ratings-card--metric-label">Based on Wealth</span>
              <ButtonTooltip
                trigger={<Icon icon="info-circle" iconKit="gt2" className="capacity-ratings-card--info-icon" />}
                contentWidth={280}
                disableArrow
              >
                Capacity Based on Wealth is DonorSearch's estimate of how much a constituent could potentially give over
                a five-year pledge period, based on publicly available wealth data such as real estate holdings and SEC
                disclosures
              </ButtonTooltip>
            </div>
            <span className="capacity-ratings-card--metric-value">
              {capacityLoading ? (
                <Icon icon="spinner" spin style={{ fontSize: 14, color: '#454545' }} />
              ) : (
                capacityWealth || '—'
              )}
            </span>
          </div>

          <div className="capacity-ratings-card--metric">
            <div className="capacity-ratings-card--metric-label-wrapper">
              <span className="capacity-ratings-card--metric-label">Based on Giving</span>
              <ButtonTooltip
                trigger={<Icon icon="info-circle" iconKit="gt2" className="capacity-ratings-card--info-icon" />}
                contentWidth={280}
                disableArrow
              >
                Capacity Based on Giving is DonorSearch's estimate of how much a constituent could potentially give
                based on their largest known charitable or political contribution to organizations other than your own,
                using publicly available giving data.
              </ButtonTooltip>
            </div>
            <span className="capacity-ratings-card--metric-value">
              {capacityLoading ? (
                <Icon icon="spinner" spin style={{ fontSize: 14, color: '#454545' }} />
              ) : (
                capacityGiving || '—'
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

CapacityRatingsCard.propTypes = {
  dsRating: PropTypes.string,
  capacityWealth: PropTypes.string,
  capacityGiving: PropTypes.string,
  capacityLoading: PropTypes.bool,
};

export default CapacityRatingsCard;
